import { pinOwnedBy } from '../contexts/WorkspaceContext';
import { FOLDER_PAGE_SIZE, folderListParams } from '../hooks/workspace/useFolderItems';
import { effectiveParams } from '../hooks/workspace/useWorkspaceViewParams';
import { phoneView } from '../pages/mobile/workspace/mobileViews';
import { apiService } from '../services/api.service';
import { listWorkspaceItems } from '../services/api/workspaceItems.api';
import {
  useWorkspaceViewPrefsStore,
  workspaceViewPrefsKey,
} from '../stores/workspaceViewPrefsStore';
import { bootListKey, putBootItemsPage } from './bootItemsSlot';

/**
 * Page 1 of the open folder, requested alongside `/auth/login` instead of after
 * it.
 *
 * Measured on staging (2026-10-04, one cold load of `/collections` from
 * Australia): Firebase hands over the ID token at ~980 ms, `/auth/login`
 * returns at ~1,400 ms, the page mounts and asks for its items at ~1,860 ms,
 * and they land at ~2,470 ms. Started with the token, they land at ~1,600 ms.
 *
 * The response waits in `bootItemsSlot.ts` (outside the query cache; the
 * module says why) for the page's own first fetch.
 *
 * WHO. Only the user the workspace pin is stamped for (`pinOwnedBy`, the same
 * rule `WorkspaceContext` uses to adopt the pin), so a shared browser never
 * asks with one person's token for another person's workspace. The params are
 * the page's own (`effectiveParams` over the URL and this user's remembered
 * view), so the page asks for the same list. The server checks membership and
 * ACL on this request like any other.
 */

/**
 * The folder a `/collections` or `/collections/:projectId` path opens: null
 * for the root, undefined for any other route.
 */
function folderOfPath(pathname: string): string | null | undefined {
  const [first, folderId, ...rest] = pathname.split('/').filter(Boolean);
  if (first !== 'collections' || rest.length > 0) return undefined;
  return folderId ?? null;
}

let started = false;

/**
 * Called with the restored session's token before `/auth/login` is awaited,
 * and acts on the first call of a page load only: a later sign-in is not a
 * boot. A no-op off a folder route or without a pin this user owns.
 */
export function startBootItemsPrefetch(uid: string, idToken: string): void {
  if (started) return;
  started = true;
  const folderId = folderOfPath(window.location.pathname);
  const workspaceId = pinOwnedBy(uid);
  if (folderId === undefined || !workspaceId) return;

  const remembered =
    useWorkspaceViewPrefsStore.getState().choices[workspaceViewPrefsKey(uid, workspaceId)];
  const view = effectiveParams(new URLSearchParams(window.location.search), remembered);
  const params = folderListParams(folderId, view);
  // The phone shell shows the view through `phoneView` (Cards becomes List),
  // and the shell is only known once the tree renders. Where that changes the
  // list (Cards under a date sort mixes folders on the phone only), the page's
  // key is unknowable here, so ask nothing rather than ask twice.
  const phone = folderListParams(folderId, { ...view, view: phoneView(view.view) });
  if (bootListKey(workspaceId, phone) !== bootListKey(workspaceId, params)) return;
  const response = listWorkspaceItems(
    apiService,
    { ...params, limit: FOLDER_PAGE_SIZE },
    undefined,
    { Authorization: `Bearer ${idToken}`, 'x-workspace-id': workspaceId },
  ).catch(() => null);
  putBootItemsPage(uid, bootListKey(workspaceId, params), response);
}

/** @internal Test seam: resets the once-per-load prefetch guard between tests. */
export function __resetBootItemsPrefetchForTests(): void {
  started = false;
}
